import { Client, type ApiClientConfig } from '@/api-clients/main-api';
import { sendSentryEvent } from './sentryHelpers';
import { useAppConfigStore } from '@/stores/appConfigStore';
import { useAuthHelper } from '@/composables/authHelper';
import router from '@/router';

// ************ CallAPI helper method (begin) ************

const authHelper = useAuthHelper();

export async function callApi<TResult>(
  method: (apiClient: Client) => Promise<TResult>,
  signal?: AbortSignal,
): Promise<TResult> {
  const apiClient = createApiClient(undefined, signal);

  try {
    return await method(apiClient);
  } catch (err) {
    //@ts-ignore
    if (err?.name === 'AbortError') {
      throw err;
    }

    //@ts-ignore
    if (err.status === 401) {
      authHelper.redirectToAuth();
      //@ts-ignore
      return;
    }

    //@ts-ignore
    if (err.status === 403) {
      router.push({ name: 'Forbidden' });
      //@ts-ignore
      return;
    }

    sendSentryEvent(err);
    throw err;
  }
}

function createApiClient(
  customizeConfig?: (callingConfig: ApiClientConfig) => void,
  signal?: AbortSignal,
): Client {
  const appConfigStore = useAppConfigStore();
  const apiBaseUrl = appConfigStore.appConfig.mainApiUrl;
  const callingConfig: ApiClientConfig = {
    defaultHeaders: {},
  };
  if (customizeConfig) {
    customizeConfig(callingConfig);
  }
  const http = signal
    ? { fetch: (url: RequestInfo, init?: RequestInit) => fetch(url, { ...init, signal }) }
    : undefined;
  const apiClient = new Client(callingConfig, apiBaseUrl, http);
  return apiClient;
}

// ************ CallAPI helper method (end) ************

// ************ Anonymous API helpers (begin) ************

function getApiBaseUrl(): string {
  const appConfigStore = useAppConfigStore();
  return appConfigStore.appConfig.mainApiUrl;
}

export async function callAnonymousGet<T>(path: string): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/${path}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw { status: response.status, ...errorBody };
  }

  return await response.json() as T;
}

export async function callAnonymousUpload<T>(path: string, formData: FormData): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/${path}`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw { status: response.status, ...errorBody };
  }

  return await response.json() as T;
}

// ************ Anonymous API helpers (end) ************
